import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMemo, useState } from 'react';
import RoutingRules from 'conversations-routing-lib/routing-rules/components/RoutingRules';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Text } from 'foundations-components/components/Text';
import { Form } from 'foundations-components/transitional/components/Form';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { SuccessIcon } from 'foundations-assets';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { Flex } from 'foundations-components/components/Flex';
// @ts-expect-error module not typed
import { trackEvent } from '../../usage-tracking/clients/usageTracker';
import { getClickedMoveToInboxCancel, getClickedMoveToInboxConfirm, getMoveToInboxAddedComment } from '../../usage-tracking/utils/threadInteraction';
import { useConversationHandoffForm } from '../hooks/useConversationHandoffForm';
import { getRoutingRuleTypeForEventTracking } from '../_internal/operators/getRoutingRuleTypeForEventTracking';
import { EMAIL_GENERIC_CHANNEL_ID } from 'conversations-thread-data/generic-channels/public/constants';
import { mapGenericChannelIdToChannelType } from 'conversations-thread-data/generic-channels/public/operators';
import { useCountOfConversationsOnAssociatedTicket } from 'conversations-thread-data/conversation-ticket-associations/public/hooks';
import { isInboxHelpdesk } from 'conversations-internal-schema/inbox/operators/inboxGetters';
import MoveToInboxAddComment from './MoveToInboxAddComment';
import MoveToInboxAddTicketSubject from './MoveToInboxAddTicketSubject';
import { useRoutingRules } from '../hooks/useRoutingRules';
import MoveToInboxSelectInbox from './MoveToInboxSelectInbox';
import { useHandleFormSubmit } from '../hooks/useHandleFormSubmit';
import { useConversationInfo } from '../hooks/useConversationInfo';
import MoveToInboxPipelineAndStatus from './MoveToInboxPipelineAndStatus';
import { HELPDESK } from 'ui-addon-conversations-settings/workspaces/constants/workspaces';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MoveToInbox = ({
  onConfirm,
  onReject,
  properties,
  isThreadFiltered
}) => {
  const [shouldDisableRoutingRules, setShouldDisableRoutingRules] = useState(false);
  const {
    assigneeId,
    associatedTicketIds,
    conversationId,
    currentInboxId,
    genericChannelId,
    inboxData,
    loadingConversationInfo,
    threadId
  } = useConversationInfo();
  const threadCurrentlyInHelpdesk = useMemo(() => {
    const threadsCurrentInbox = inboxData && inboxData.find(inbox => inbox.id === currentInboxId);
    return Boolean(threadsCurrentInbox && isInboxHelpdesk(threadsCurrentInbox));
  }, [inboxData, currentInboxId]);
  const {
    totalConversationsCount,
    loading: conversationCountLoading
  } = useCountOfConversationsOnAssociatedTicket({
    /*  @ts-expect-error Jun-5-2024, 19:34UTC TODO: fix broken types introduced from typing of ThreadDetails record: https://git.hubteam.com/HubSpot/conversations-thread-view/pull/3656  */
    conversationId,
    skip: threadCurrentlyInHelpdesk
  });
  const {
    handleFormSubmitCompleted,
    handleFormSubmitError
  } = useHandleFormSubmit({
    onConfirm,
    onReject,
    threadId
  });
  const {
    setFieldValue: {
      setComment,
      setInbox,
      setRoutingRuleDefinition,
      setSubject,
      setPipelineInformation
    },
    submitForm,
    isValid,
    comment,
    pipelineInformation,
    selectedInboxId,
    routingRuleDefinition,
    isToHelpdesk,
    subject,
    shouldCreateNewTicket
  } = useConversationHandoffForm({
    /*  @ts-expect-error Jun-5-2024, 19:34UTC TODO: fix broken types introduced from typing of ThreadDetails record: https://git.hubteam.com/HubSpot/conversations-thread-view/pull/3656  */
    associatedTicketIds,
    genericChannelId,
    threadId: `${threadId}`,
    options: {
      onCompleted: handleFormSubmitCompleted,
      onError: handleFormSubmitError
    },
    threadCurrentlyInHelpdesk
  });
  const handleFormOnSubmit = evt => {
    evt.preventDefault();
    submitForm();
  };
  const handleOnConfirmMove = () => {
    if (comment.length > 0) {
      trackEvent(getMoveToInboxAddedComment());
    }
    const type = getRoutingRuleTypeForEventTracking(routingRuleDefinition);
    trackEvent(getClickedMoveToInboxConfirm({
      type
    }));
    submitForm();
  };
  const handleOnCancel = () => {
    trackEvent(getClickedMoveToInboxCancel());
    onReject();
  };
  const {
    handleRoutingRuleOnOpen,
    baseRoutingRulesProps,
    userInfoDataIsLoading,
    canKeepAssignee
  } = useRoutingRules({
    assigneeId,
    selectedInboxId,
    routingRuleDefinition,
    setRoutingRuleDefinition
  });
  const loading = loadingConversationInfo || userInfoDataIsLoading || conversationCountLoading;
  const isInboxToInboxMove = !threadCurrentlyInHelpdesk && !isToHelpdesk;
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "move-to-inbox-dialog",
    width: 550,
    children: [/*#__PURE__*/_jsx(DialogHeader, {
      children: /*#__PURE__*/_jsxs(Text, {
        as: "h2",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-thread-view.moveToInboxPrompt.heading"
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: handleOnCancel
        })]
      })
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [loading ? /*#__PURE__*/_jsx(LoadingOverlay, {
        contextual: true
      }) : null, /*#__PURE__*/_jsxs(Form, {
        onSubmit: handleFormOnSubmit,
        children: [/*#__PURE__*/_jsx(FormControl, {
          marginBlockEnd: "400",
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "conversations-thread-view.moveToInboxPrompt.inputTitle"
          }),
          required: true,
          children: /*#__PURE__*/_jsx(MoveToInboxSelectInbox, {
            currentInboxId: currentInboxId,
            inboxData: inboxData,
            loading: loading,
            selectedInboxId: selectedInboxId,
            setShouldDisableRoutingRules: setShouldDisableRoutingRules,
            setRoutingRuleDefinition: setRoutingRuleDefinition,
            setInbox: setInbox,
            threadCurrentlyInHelpdesk: threadCurrentlyInHelpdesk,
            totalConversationsCount: totalConversationsCount,
            isThreadFiltered: isThreadFiltered
          })
        }), shouldCreateNewTicket && /*#__PURE__*/_jsx(MoveToInboxAddTicketSubject, {
          subject: subject,
          setSubject: setSubject
        }), (isToHelpdesk || threadCurrentlyInHelpdesk) && /*#__PURE__*/_jsx(MoveToInboxPipelineAndStatus, {
          properties: properties,
          setPipelineInformation: setPipelineInformation,
          pipelineInformation: pipelineInformation
        }), baseRoutingRulesProps && !shouldDisableRoutingRules && /*#__PURE__*/_jsx(RoutingRules, Object.assign({}, baseRoutingRulesProps, {
          disabled: !selectedInboxId,
          FormControl: FormControl,
          shouldFetchTeams: true,
          showAnyUserOption: true,
          showKeepAssigneeOption: !isToHelpdesk,
          showOwnerOption: isInboxToInboxMove,
          disableKeepAssigneeOption: !canKeepAssignee,
          onOpenChange: handleRoutingRuleOnOpen,
          use: mapGenericChannelIdToChannelType(genericChannelId),
          hasEditAccess: true,
          hasRoutingAccess: true,
          workSpace: threadCurrentlyInHelpdesk || isToHelpdesk ? HELPDESK : undefined
        })), /*#__PURE__*/_jsx(MoveToInboxAddComment, {
          comment: comment,
          setComment: setComment
        })]
      }), /*#__PURE__*/_jsxs(List, {
        marginBlockStart: "400",
        children: [isToHelpdesk && !shouldCreateNewTicket && /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsxs(Flex, {
            children: [/*#__PURE__*/_jsx(IconHolder, {
              icon: SuccessIcon,
              color: "fill-positive-default"
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "conversations-thread-view.moveToInboxPrompt.toHelpDeskHelpTexts.withTicketHelpText"
            })]
          })
        }), threadCurrentlyInHelpdesk && /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsxs(Flex, {
            children: [/*#__PURE__*/_jsx(IconHolder, {
              icon: SuccessIcon,
              color: "fill-positive-default"
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "conversations-thread-view.moveToInboxPrompt.toHelpDeskHelpTexts.fromHelpDeskText"
            })]
          })
        }), isInboxToInboxMove && /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsxs(Flex, {
            children: [/*#__PURE__*/_jsx(IconHolder, {
              icon: SuccessIcon,
              color: "fill-positive-default"
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "conversations-thread-view.moveToInboxPrompt.helpTextFour"
            })]
          })
        }), shouldDisableRoutingRules && /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsxs(Flex, {
            children: [/*#__PURE__*/_jsx(IconHolder, {
              icon: SuccessIcon,
              color: "fill-positive-default"
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "conversations-thread-view.moveToInboxPrompt.toHelpDeskHelpTexts.unassignText"
            })]
          })
        }), genericChannelId !== EMAIL_GENERIC_CHANNEL_ID && !shouldDisableRoutingRules && /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsxs(Flex, {
            children: [/*#__PURE__*/_jsx(IconHolder, {
              icon: SuccessIcon,
              color: "fill-positive-default"
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "conversations-thread-view.moveToInboxPrompt.helpTextFive"
            })]
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Button, {
        "data-test-id": "move-to-inbox-confirm-button",
        use: "primary",
        disabled: !isValid,
        onClick: handleOnConfirmMove,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-thread-view.moveToInboxPrompt.confirm"
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "tertiary-light",
        onClick: handleOnCancel,
        "data-test-id": "move-to-inbox-prompt-cancel-button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-thread-view.moveToInboxPrompt.cancel"
        })
      })]
    })]
  });
};
MoveToInbox.displayName = 'MoveToInbox';
export default MoveToInbox;